<script setup lang="ts">
import { toast } from 'vue-sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import AppMaskedDateInput from '@/components/form/AppMaskedDateInput.vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { getLoginPath } from '@/composables/auth-redirect'
import { formatShortDate, responseStatusTitle } from '@/composables/new-ui'
import { useClient } from '@/stores/client'

type ResponseDetails = {
  id: string
  status: number
  answered: boolean
  created: number
  isArchive: boolean
  client: { id: string }
  summary: { id: string, status: number }
  vacancy: { id: string, title: string, company: { id: string } } | null
  chat: { id: string } | null
  colloquy: { id: string, startTime: number, platform: number, location?: string, description?: string } | null
}
type Answer = { id: string, question: string, answer?: { src?: string | null } | null }
type ActionForm = 'question' | 'interview' | 'offer' | 'reject' | 'edit' | null

const props = defineProps<{ responseId: string, listingId: string, kind: 'vacancy' | 'summary' }>()
const emit = defineEmits<{ changed: [status: number] }>()
const client = useClient()
const { hook } = useNuxtApp()
const route = useRoute()
const response = ref<ResponseDetails | null>(null)
const loading = ref(true)
const error = ref('')
const busy = ref(false)
const form = ref<ActionForm>(null)
const question = ref('')
const message = ref('')
const rejectMessage = ref('')
// The candidate reads the rejection text in the chat; templates only fill the field.
const rejectTemplates = [
  { label: 'Не готовы пригласить', text: 'Спасибо за отклик! К сожалению, сейчас мы не готовы пригласить вас на следующий этап.' },
  { label: 'Выбрали другого', text: 'Спасибо за интерес к вакансии. Мы выбрали кандидата, чей опыт ближе к задачам этой позиции.' },
  { label: 'Вакансия закрыта', text: 'Спасибо за отклик! Вакансия уже закрыта, но мы сохраним ваше резюме и напишем, если появится подходящая позиция.' },
]
const interviewDate = ref('')
const platform = ref('1')
const location = ref('SOBES.RU')
const description = ref('')
const answers = ref<Answer[]>([])
const answersError = ref('')
const answersLoading = ref(false)
const showAnswers = ref(false)
const isApplicant = computed(() => response.value?.client.id === client.getClientId)
const active = computed(() => response.value && !response.value.isArchive && response.value.summary.status === 2 && ![3, 7, 8].includes(response.value.status))
let requestId = 0
let answersRequestId = 0
let disposed = false
let removeChatHook: (() => void) | undefined

async function load(autoReview = true, preserveForm = false) {
  const current = ++requestId
  if (!preserveForm) loading.value = true
  error.value = ''
  if (!preserveForm) form.value = null
  if (!preserveForm) {
    answersRequestId++
    showAnswers.value = false
    answers.value = []
    answersError.value = ''
  }
  const result = await mainFetch(`responses/${props.responseId}`)
  if (current !== requestId || disposed) return
  const data = result.data.value?.response as ResponseDetails | undefined
  if (result.error.value || !data) error.value = apiErrorMessage(result.error.value, 'Не удалось загрузить отклик')
  else if ((props.kind === 'vacancy' ? data.vacancy?.id : data.summary?.id) !== props.listingId) error.value = 'Этот отклик относится к другой публикации'
  else response.value = data
  if (error.value) response.value = null
  if (!active.value) form.value = null
  loading.value = false
  if (autoReview && response.value?.status === 1 && !isApplicant.value && active.value && !busy.value) await updateStatus(2, undefined, true)
}

async function loadAnswers() {
  const current = ++answersRequestId
  answersLoading.value = true
  answersError.value = ''
  const result = await mainFetch(`responses/${props.responseId}/answers`)
  if (current !== answersRequestId || disposed) return
  if (result.error.value) answersError.value = apiErrorMessage(result.error.value, 'Не удалось загрузить видеоответы')
  else answers.value = result.data.value?.additionQuestions ?? []
  answersLoading.value = false
}

function toggleAnswers() {
  showAnswers.value = !showAnswers.value
  if (showAnswers.value) void loadAnswers()
}

// Keep the actual response visible after a mutation:
// returning to the list reloads its statuses, while a reload here remains truthful.
async function updateStatus(status: number, statusMessage?: string, silent = false) {
  if (!response.value || busy.value) return
  busy.value = true
  const result = await mainFetch(`responses/${props.responseId}`, { method: 'PUT', body: { status, ...(statusMessage !== undefined ? { message: statusMessage } : {}) } })
  if (disposed) return
  if (result.error.value) {
    if (!silent) toast.error(apiErrorMessage(result.error.value, 'Не удалось изменить статус отклика'))
    await load(false, true)
  }
  else {
    if (status === 3) rejectMessage.value = ''
    await load(false)
    emit('changed', status)
    if (!silent) toast.success(status === 2 ? 'Отклик отмечен просмотренным' : status === 3 ? 'Кандидату отказано' : 'Статус отклика обновлён')
  }
  busy.value = false
}

function openForm(next: ActionForm) {
  form.value = next
  if (next === 'interview' || next === 'edit') {
    const interview = next === 'edit' ? response.value?.colloquy : null
    const date = new Date(interview ? interview.startTime * 1000 : Date.now() + 86400000)
    interviewDate.value = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
    platform.value = String(interview?.platform ?? 1)
    location.value = interview?.location ?? 'SOBES.RU'
    description.value = interview?.description ?? ''
  }
}

async function sendQuestion() {
  if (!question.value.trim() || busy.value) return
  busy.value = true
  const result = await mainFetch(`responses/${props.responseId}/additional-question`, { method: 'POST', body: { question: question.value.trim() } })
  if (disposed) return
  if (result.error.value) {
    toast.error(apiErrorMessage(result.error.value, 'Не удалось отправить вопрос'))
    await load(false, true)
  }
  else {
    question.value = ''
    await load()
    emit('changed', 4)
    toast.success('Вопрос отправлен кандидату')
  }
  busy.value = false
}

async function saveInterview() {
  const startTime = Math.floor(new Date(interviewDate.value).getTime() / 1000)
  if (!response.value?.vacancy || busy.value || !Number.isFinite(startTime) || !location.value.trim()) return
  const editing = form.value === 'edit'
  const interview = response.value.colloquy
  if (editing && !interview) return
  busy.value = true
  const result = await mainFetch(editing ? `colloquies/${interview!.id}` : `vacancies/${response.value.vacancy.id}/colloquies`, {
    method: editing ? 'PUT' : 'POST',
    body: { ...(!editing ? { response: props.responseId } : {}), startTime, platform: Number(platform.value), location: location.value.trim(), description: description.value },
  })
  if (disposed) return
  if (result.error.value) {
    toast.error(apiErrorMessage(result.error.value, 'Не удалось сохранить собеседование'))
    await load(false, true)
  }
  else {
    await load()
    emit('changed', response.value?.status ?? 5)
    toast.success(editing ? 'Собеседование изменено' : 'Приглашение на собеседование отправлено')
  }
  busy.value = false
}

watch(() => [props.responseId, props.listingId, client.isAuthenticated], () => {
  if (client.isAuthenticated) void load()
})
async function refreshOnFocus() {
  if (!client.isAuthenticated || busy.value || loading.value) return
  const previousStatus = response.value?.status
  await load(false, true)
  if (!disposed && response.value && response.value.status !== previousStatus) emit('changed', response.value.status)
}
onMounted(() => {
  removeChatHook = hook('chat', (data: { event: string, chat?: { response?: { id: string } } }) => {
    if (data.event === 'responseStatus' && data.chat?.response?.id === props.responseId) void refreshOnFocus()
  })
  if (client.isAuthenticated) void load()
  window.addEventListener('focus', refreshOnFocus)
})
onBeforeUnmount(() => {
  removeChatHook?.()
  disposed = true
  requestId++
  answersRequestId++
  window.removeEventListener('focus', refreshOnFocus)
})
</script>

<template>
  <section aria-label="Действия с откликом" class="flex flex-col gap-4">
    <Alert v-if="!client.isAuthenticated">
      <AlertTitle>Войдите, чтобы посмотреть отклик</AlertTitle>
      <AlertDescription><Button as-child variant="link"><NuxtLink :to="getLoginPath(route.fullPath)">Войти</NuxtLink></Button></AlertDescription>
    </Alert>
    <Skeleton v-else-if="loading" class="h-24 rounded-2xl" aria-label="Загрузка отклика" />
    <Alert v-else-if="error" variant="destructive">
      <AlertTitle>Отклик недоступен</AlertTitle>
      <AlertDescription class="flex flex-col gap-2"><p>{{ error }}</p><Button variant="outline" class="self-start" @click="load()">Повторить</Button></AlertDescription>
    </Alert>
    <template v-else-if="response">
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="secondary">{{ responseStatusTitle(response) }}</Badge>
        <span class="text-muted-foreground">Отклик от {{ formatShortDate(response.created) }}</span>
      </div>
      <p v-if="kind === 'summary'" class="text-sm text-muted-foreground">Отклик на вакансию: {{ response.vacancy?.title || 'Вакансия недоступна' }}</p>
      <p v-if="!response.isArchive && response.summary.status !== 2" class="text-sm text-muted-foreground">Резюме снято с публикации. Действия с откликом недоступны.</p>
      <p v-if="response.isArchive" class="text-sm text-muted-foreground">Отклик в архиве. Изменение статуса недоступно.</p>
      <div v-if="response.colloquy" class="flex flex-col gap-1 text-sm">
        <p class="font-medium">Собеседование · {{ new Date(response.colloquy.startTime * 1000).toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' }) }}</p>
        <p>{{ response.colloquy.platform === 1 ? 'Онлайн' : 'В офисе' }} · {{ response.colloquy.location }}</p>
        <p v-if="response.colloquy.description">{{ response.colloquy.description }}</p>
      </div>
      <div v-if="!form" class="flex flex-wrap gap-2">
        <template v-if="active">
          <template v-if="isApplicant">
            <Button v-if="response.status === 4 && !response.answered" as-child shape="pill"><NuxtLink :to="{ path: '/applicant/questions', query: { response: responseId } }">Записать доп. ответы</NuxtLink></Button>
          </template>
          <template v-else>
            <Button v-if="response.status === 1" variant="outline" shape="pill" :disabled="busy" @click="updateStatus(2)">Просмотрено</Button>
            <Button v-if="[1, 2].includes(response.status)" variant="outline" shape="pill" :disabled="busy" @click="openForm('question')">Задать вопрос</Button>
            <Button v-if="[1, 2, 4].includes(response.status)" variant="outline" shape="pill" :disabled="busy" @click="openForm('interview')">Собеседование</Button>
            <Button v-if="[1, 2, 4, 5, 6].includes(response.status)" shape="pill" :disabled="busy" @click="openForm('offer')">Оффер</Button>
            <Button v-if="[1, 2].includes(response.status)" variant="outline" shape="pill" :disabled="busy" @click="openForm('reject')">Отказать</Button>
            <Button v-if="response.colloquy" variant="outline" shape="pill" :disabled="busy" @click="openForm('edit')">Изменить</Button>
          </template>
        </template>
        <Button v-if="response.chat" as-child variant="outline" shape="pill"><NuxtLink :to="{ path: '/chats', query: { id: response.chat.id } }">Чат</NuxtLink></Button>
        <Button variant="outline" shape="pill" :aria-expanded="showAnswers" @click="toggleAnswers">Доп. видеоответы</Button>
      </div>

      <Card v-if="form" size="sm">
        <CardHeader><CardTitle>{{ form === 'question' ? 'Задать дополнительный вопрос' : form === 'offer' ? 'Пригласить на работу' : form === 'reject' ? 'Отказать кандидату' : form === 'edit' ? 'Редактировать собеседование' : 'Пригласить на собеседование' }}</CardTitle></CardHeader>
        <CardContent>
          <FieldGroup v-if="form === 'question'"><Field><FieldLabel for="response-question">Вопрос кандидату</FieldLabel><Textarea id="response-question" v-model="question" maxlength="1000" placeholder="Напишите вопрос кандидату" :disabled="busy" /></Field></FieldGroup>
          <FieldGroup v-else-if="form === 'offer'"><Field><FieldLabel for="response-offer">Комментарий для кандидата</FieldLabel><Textarea id="response-offer" v-model="message" maxlength="200" :disabled="busy" /></Field></FieldGroup>
          <FieldGroup v-else-if="form === 'reject'">
            <div role="group" aria-label="Шаблоны отказа" class="flex flex-wrap gap-2"><Button v-for="template in rejectTemplates" :key="template.label" type="button" variant="outline" size="sm" shape="pill" :disabled="busy" :aria-pressed="rejectMessage === template.text" @click="rejectMessage = template.text">{{ template.label }}</Button></div>
            <Field><FieldLabel for="response-reject">Сообщение кандидату</FieldLabel><Textarea id="response-reject" v-model="rejectMessage" maxlength="1000" placeholder="Необязательно. Кандидат увидит его в чате" :disabled="busy" /></Field>
          </FieldGroup>
          <FieldGroup v-else>
            <Field><FieldLabel for="response-date" required>Дата и время</FieldLabel><AppMaskedDateInput id="response-date" v-model="interviewDate" time :disabled="busy" /></Field>
            <ToggleGroup :model-value="platform" type="single" :disabled="busy" @update:model-value="value => { if (value === '0' || value === '1') platform = value }"><ToggleGroupItem value="1">Онлайн</ToggleGroupItem><ToggleGroupItem value="0">В офисе</ToggleGroupItem></ToggleGroup>
            <Field><FieldLabel for="response-location" required>{{ platform === '0' ? 'Адрес' : 'Площадка' }}</FieldLabel><Input id="response-location" v-model="location" :disabled="busy" /></Field>
            <Field><FieldLabel for="response-description">Детали</FieldLabel><Textarea id="response-description" v-model="description" :disabled="busy" /></Field>
          </FieldGroup>
        </CardContent>
        <CardFooter class="flex flex-wrap gap-2">
          <Button v-if="form === 'question'" :disabled="busy || !question.trim()" @click="sendQuestion">Отправить</Button>
          <Button v-else-if="form === 'offer'" :disabled="busy" @click="updateStatus(7, message)">Отправить оффер</Button>
          <Button v-else-if="form === 'reject'" variant="destructive" :disabled="busy" @click="updateStatus(3, rejectMessage.trim() || undefined)">Отказать</Button>
          <Button v-else :disabled="busy || !interviewDate || !location.trim()" @click="saveInterview">{{ form === 'edit' ? 'Сохранить' : 'Пригласить' }}</Button>
          <Button variant="outline" :disabled="busy" @click="form = null">Отмена</Button>
        </CardFooter>
      </Card>

      <section v-if="showAnswers" aria-label="Дополнительные видеоответы" class="flex flex-col gap-3">
        <Skeleton v-if="answersLoading" class="h-24 rounded-2xl" />
        <Alert v-else-if="answersError" variant="destructive"><AlertTitle>Видеоответы недоступны</AlertTitle><AlertDescription class="flex flex-col gap-2"><p>{{ answersError }}</p><Button variant="outline" class="self-start" @click="loadAnswers">Повторить</Button></AlertDescription></Alert>
        <template v-else-if="answers.length">
          <Card v-for="answer in answers" :key="answer.id" size="sm" data-additional="true">
            <CardHeader><p class="text-sm text-muted-foreground">{{ isApplicant ? 'Вопрос работодателя' : 'Ваш вопрос' }}</p><CardTitle>{{ answer.question }}</CardTitle></CardHeader>
            <CardContent><video v-if="answer.answer?.src" :src="answer.answer.src" controls playsinline preload="metadata" class="max-h-64 w-full rounded-xl bg-black" /><p v-else class="text-sm text-muted-foreground">{{ isApplicant ? 'Ответ ещё не записан' : 'Кандидат ещё не записал ответ' }}</p></CardContent>
          </Card>
        </template>
        <p v-else class="text-sm text-muted-foreground">Дополнительных видеоответов пока нет</p>
      </section>
    </template>
  </section>
</template>
